import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { storage } from '@doxyme/ui/utils';
import { updateLoggerContext } from '@doxyme/analytics';
import { RealtimeRole } from '@doxyme/realtime';
import type { RoomV2 } from '../../services/room';
import { StorageKeys } from '../../shared/constants';
import type { ApolloSession } from './apolloSession.types';

const PROPS_TO_KEEP = [
  'role', // `role` is needed to check if the redirect from OG (Angular app) to V2 (this app) was correct
  'referrer', // `referrer` to know where to redirect
  'roomId',
  'dbSessionId',
  'sessionId',
  'vendor', // `roomId`, `dbSessionId` and `vendor` are needed for OG to track system for 'Call Rated' event
] as (keyof ApolloSession)[];

export const emptyInitialState: ApolloSession = {
  rooms: [],
  passcode: undefined,
  sessionId: undefined,
  vendor: undefined,
  patientId: undefined,
  role: undefined,
  referrer: '',
  state: {},
  navigationStarted: undefined,
  roomId: undefined,
  identityId: undefined,
  institutionsMembershipsIds: [],
  institutionId: undefined,
  profileInfo: undefined,
  userId: undefined,
  patientPlatform: undefined,
  dbSessionId: undefined,
  isAudioCall: false,
  startCallTime: 0,
};

const getApolloSessionFromStore = (): ApolloSession => {
  return typeof window !== 'undefined' // Should only run in the browser
    ? (storage.getItem<ApolloSession>(StorageKeys.ApolloSession) ??
        emptyInitialState)
    : emptyInitialState;
};

/**
 * Remove all apolloSession props except props provided as 2nd param
 * We still need to keep some props to distinguish wrong arrival on the page from refresh
 */
export const setClearedApolloSessionToStore = (
  apolloSession: ApolloSession,
  propsToKeep = PROPS_TO_KEEP,
): void => {
  const clearedApolloSession = Object.fromEntries(
    propsToKeep.map((prop) => [prop, apolloSession[prop]]),
  );

  storage.setItem(StorageKeys.ApolloSession, clearedApolloSession);
};

export const apolloSessionSlice = createSlice({
  name: 'apolloSession',
  initialState: emptyInitialState,
  reducers: {
    update: (state, action: PayloadAction<Partial<ApolloSession>>) => {
      const newState = { ...state, ...action.payload };
      setClearedApolloSessionToStore(newState);
      updateLoggerContext({ sessionId: action.payload.sessionId });

      return newState;
    },
    setDbSessionId: (state, action: PayloadAction<string>) => {
      const dbSessionId = action.payload;
      state.dbSessionId = dbSessionId;
      setClearedApolloSessionToStore({
        ...state,
      });
    },
    clearDbSessionId: (state) => {
      state.dbSessionId = undefined;

      setClearedApolloSessionToStore({
        ...state,
      });
    },
    setSessionId: (state, action: PayloadAction<string | undefined>) => {
      updateLoggerContext({ sessionId: action.payload });

      state.sessionId = action.payload;
      setClearedApolloSessionToStore({
        ...state,
      });
    },
    setStartCallTime: (state, action: PayloadAction<number>) => {
      state.startCallTime = action.payload;
    },
    setIsAudioCall: (state, action: PayloadAction<boolean>) => {
      state.isAudioCall = action.payload;
    },
    initialize: (
      state,
      action: PayloadAction<{
        role?: RealtimeRole.Provider | RealtimeRole.Patient | undefined;
        rooms?: RoomV2[] | undefined;
        userId?: string;
      }>,
    ) => {
      const apolloSession = getApolloSessionFromStore();
      setClearedApolloSessionToStore(apolloSession);
      Object.assign(state, apolloSession);

      state.state.legacyIdentityId = apolloSession.identityId;
      state.userId = Boolean(action.payload.userId)
        ? Number(action.payload.userId)
        : undefined;

      /**
       * Determine the role by the route,
       * else get the role from sessionStorage,
       * else assume they are a provider.
       */
      state.role = action.payload.role ?? state.role ?? RealtimeRole.Provider;

      /**
       * Get the rooms by the route,
       * else get the role from sessionStorage.
       */
      state.rooms = action.payload.rooms ?? state.rooms;
    },
    clearApolloSession: (state) => {
      const referrer = state.referrer;
      Object.assign(state, emptyInitialState);
      updateLoggerContext({ sessionId: '' });

      state.isV2 = false;
      state.referrer = referrer;
      setClearedApolloSessionToStore({
        ...state,
      });
    },
  },
});

export const {
  initialize,
  update,
  setDbSessionId,
  setIsAudioCall,
  setSessionId,
  setStartCallTime,
  clearDbSessionId,
  clearApolloSession,
} = apolloSessionSlice.actions;

export default apolloSessionSlice.reducer;
